var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { isFilled, isFree } from "./types";
function last(array) {
    return array[array.length - 1];
}
function withoutLast(array) {
    return array.slice(0, array.length - 1);
}
export function generateHintsFor(line) {
    var hints = line.reduce(function (previous, current) {
        var lastPrevious = last(previous);
        if (current === "filled") {
            return __spreadArray(__spreadArray([], withoutLast(previous), true), [lastPrevious + 1], false);
        }
        else if (lastPrevious) {
            return __spreadArray(__spreadArray([], previous, true), [0], false);
        }
        else {
            return __spreadArray([], previous, true);
        }
    }, [0]);
    if (hints.length > 1 && last(hints) === 0) {
        return withoutLast(hints);
    }
    else {
        return hints;
    }
}
/**
 * Retrieve row and column hint numbers and return them.
 *
 * @return {Hints} Return Hints object containing two arrays (rows[], cols[]).
 */
export function generateHints(board) {
    var metaMatrix = board.metaMatrix;
    var hintNumbers = {
        rows: Array.from(Array(metaMatrix.rows).keys()).map(function (row) {
            return generateHintsFor(board.getRow(row));
        }),
        cols: Array.from(Array(metaMatrix.cols).keys()).map(function (col) {
            return generateHintsFor(board.getCol(col));
        })
    };
    return hintNumbers;
}
function appliableLineAnalysisForHint(hint, completeLineAnalysis) {
    var tailRecursion = function (hint, lineAnalysis, acc) {
        if (lineAnalysis.length === 0) {
            return acc;
        }
        var _a = lineAnalysis[0], count = _a[0], type = _a[1];
        if (isFilled(type) && count === hint) {
            return acc.concat([lineAnalysis]);
        }
        else if (isFilled(type) && count !== hint) {
            return tailRecursion(hint, __spreadArray([
                [hint, "free_but"]
            ], lineAnalysis
                .slice(1)
                .map(function (_a, i) {
                var count = _a[0], type = _a[1];
                if (isFree(type)) {
                    return [count, "free_but"];
                }
                else if (i === 0) {
                    return [count, "filled_but"];
                }
                else {
                    return [count, type];
                }
            }), true), acc);
        }
        else if (isFree(type) && count >= hint) {
            return tailRecursion(hint, __spreadArray([[count - hint - 1, type]], lineAnalysis.slice(1), true), acc.concat([lineAnalysis]));
        }
        else {
            return tailRecursion(hint, lineAnalysis.slice(1), acc);
        }
    };
    return tailRecursion(hint, completeLineAnalysis, []);
}
function applyHintToLineAnalysis(hint, lineAnalysis) {
    var first = lineAnalysis[0], others = lineAnalysis.slice(1);
    var count = first[0], type = first[1];
    if (count === hint && type === "filled") {
        return [true, others];
    }
    else if (count === hint && type === "filled_but") {
        return ["true_but", others];
    }
    else {
        var newCount = count - hint - 1;
        var newType = type;
        var crossout = type === "free" ? false : "false_but";
        if (newCount <= 0) {
            return [crossout, others];
        }
        else {
            return [crossout, __spreadArray([[newCount, newType]], others, true)];
        }
    }
}
function computePossibleCrossouts(goalHints, lineAnalysis) {
    var recursion = function (remainingHints, remainingLineAnalysis, current) {
        var firstHint = remainingHints[0], lastHints = remainingHints.slice(1);
        if (firstHint === undefined) {
            if (current.every(function (c) { return c === true || c === "true_but"; })) {
                return [current.map(function (c) { return (c === "true_but" ? true : c); })];
            }
            else if (remainingLineAnalysis.every(function (la) { return !isFilled(la[1]); })) {
                return [current];
            }
            else {
                return [];
            }
        }
        else {
            var usableLineAnalysis = remainingLineAnalysis.slice(remainingLineAnalysis.findIndex(function (_a) {
                var count = _a[0], type = _a[1];
                return (!isFilled(type) && count >= remainingHints[0]) || isFilled(type);
            }));
            var appliables = appliableLineAnalysisForHint(firstHint, usableLineAnalysis);
            return appliables.flatMap(function (appliableLA) {
                var _a = applyHintToLineAnalysis(firstHint, appliableLA), crossout = _a[0], next = _a[1];
                return recursion(lastHints, next, current.concat(crossout));
            });
        }
    };
    return recursion(goalHints, lineAnalysis, []);
}
function computeAndAggregateCrossouts(goalHints, lineAnalysis) {
    if (goalHints[0] === 0) {
        return [true];
    }
    var allCrossoutsByLine = computePossibleCrossouts(goalHints, lineAnalysis);
    if (allCrossoutsByLine.length === 0) {
        return goalHints.map(function () { return false; });
    }
    else {
        var butCrossoutsByLine = allCrossoutsByLine.map(function (line) {
            return [
                line,
                line.filter(function (e) { return e === "true_but"; }).length,
                line.filter(function (e) { return e === "false_but"; }).length
            ];
        });
        var minButInALine_1 = butCrossoutsByLine.reduce(function (previous, _a) {
            var _ = _a[0], countTrueBut = _a[1], countFalseBut = _a[2];
            var sum = countTrueBut + countFalseBut;
            return sum < previous ? sum : previous;
        }, Number.MAX_SAFE_INTEGER);
        var minButCrossoutsByLine = butCrossoutsByLine.filter(function (_a) {
            var _ = _a[0], countTrueBut = _a[1], countFalseBut = _a[2];
            return countTrueBut + countFalseBut === minButInALine_1;
        });
        var maxTrueButInALine_1 = minButCrossoutsByLine.reduce(function (previous, _a) {
            var _ = _a[0], countTrueBut = _a[1];
            return countTrueBut > previous ? countTrueBut : previous;
        }, Number.MIN_SAFE_INTEGER);
        var maxTrueButCrossoutsByLine = minButCrossoutsByLine
            .filter(function (_a) {
            var _ = _a[0], countTrueBut = _a[1];
            return countTrueBut === maxTrueButInALine_1;
        })
            .map(function (_a) {
            var crossouts = _a[0];
            return crossouts;
        });
        return maxTrueButCrossoutsByLine
            .reduce(function (previous, current) {
            return previous.map(function (_, i) {
                return !(previous[i] === false ||
                    current[i] === false ||
                    previous[i] === "false_but" ||
                    current[i] === "false_but");
            });
        }, goalHints.map(function () { return "true_but"; }))
            .map(function (crossout) {
            if (crossout === "false_but") {
                return false;
            }
            else if (crossout === "true_but") {
                return true;
            }
            else {
                return crossout;
            }
        });
    }
}
export function computeCrossoutLine(goalHints, lineAnalysis) {
    var crossout = computeAndAggregateCrossouts(goalHints, lineAnalysis);
    return {
        completed: crossout.every(function (c) { return c; }),
        line: goalHints.map(function (_, i) { return ({
            crossout: crossout[i],
            hint: goalHints[i]
        }); }),
        overflow: (goalHints[0] === 0 &&
            lineAnalysis.filter(function (la) { return la[1] === "filled"; }).length > 0) ||
            lineAnalysis.filter(function (la) { return la[1] === "filled"; }).length > goalHints.length
    };
}
export function analyzeLine(line) {
    var analysisItems = line.reduce(function (previous, current) {
        var _a = last(previous), lastCount = _a[0], lastType = _a[1];
        if (current === "empty") {
            if (lastType === "filled") {
                return __spreadArray(__spreadArray([], previous, true), [[0, "free"]], false);
            }
            else {
                return __spreadArray(__spreadArray([], withoutLast(previous), true), [[lastCount + 1, "free"]], false);
            }
        }
        else if (current === "marked") {
            return __spreadArray(__spreadArray([], previous, true), [[0, "free"]], false);
        }
        else {
            if (lastType === "filled") {
                return __spreadArray(__spreadArray([], withoutLast(previous), true), [[lastCount + 1, "filled"]], false);
            }
            else {
                return __spreadArray(__spreadArray([], withoutLast(previous), true), [
                    [lastCount - 1, "free"],
                    [1, "filled"]
                ], false);
            }
        }
    }, [[0, "free"]]);
    return analysisItems.filter(function (i) { return i[0] > 0; });
}
export function generateCrossoutFor(line, hints) {
    return computeCrossoutLine(hints, analyzeLine(line));
}
